/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  useCallback,
} from "react";

import { useGetCartQuery } from "@/api/AppApi/services/cart";
import {
  CartItemTypes,
  ICartItem_Product,
  ICartItemType,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { IBundle } from "@/api/AppApi/services/home/interfaces/IProductBundle";
import useSellingMode from "@/hooks/useSellingMode";
import { compareAddOnsArrays } from "@/hooks/product-has-attrs/product-has-attrs-helpers/functions";
import { ISelectedBundleItems } from "@/hooks/product-has-attrs/product-has-attrs-helpers/interfaces";
import {
  compareBundleItems,
  generateBundleItems,
  groupBundleItemsByBundleItemId,
  ungroupBundleItems,
} from "@/app/components/pages-components/product-details-page/bundle/functions";

interface BundleProductContextType {
  product: IBundle;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  addedToCart: boolean;
  setAddedToCart: Dispatch<SetStateAction<boolean>>;
  enableAddToCart: boolean;
  showAddToCardBtn?: boolean;
  viewDetails: boolean;
  mustViewDetails?: boolean;
  add_to_cart_btn_title?: string;
  cartItemId?: number;
  outOfStock: boolean;
  isDisabled: boolean;
  currentProductInCart: {
    quantity: number;
    addedToCart: boolean;
    id?: number;
  };
  selectedBundleItems: ISelectedBundleItems[];
  setSelectedBundleItems: Dispatch<SetStateAction<ISelectedBundleItems[]>>;
  hanldeChildChange: (item_index: number, index: number | null) => void;
  notAllBundleItemsChildrenSelected: boolean;
}

const BundleProductContext = createContext<
  BundleProductContextType | undefined
>(undefined);

const getProductFromCart = (
  cartItems: ICartItemType[],
  product: IBundle,
  selectedBundleItems: ISelectedBundleItems[],
) => {
  const index = (cartItems ?? [])?.findIndex((item) => {
    const basicMatch =
      item.inner_id === product?.id &&
      compareAddOnsArrays(item.addons, []) &&
      !item.note;

    // If it's a product type, also check bundle items
    if (item.type === CartItemTypes.product) {
      const currentBundleItems =
        groupBundleItemsByBundleItemId(selectedBundleItems);
      const itemBundleItems = item.bundle_items || [];

      return (
        basicMatch && compareBundleItems(currentBundleItems, itemBundleItems)
      );
    }

    return basicMatch;
  });
  if (index < 0)
    return {
      quantity: 0,
      addedToCart: false,
      id: undefined,
      bundle_items: [],
    };
  return {
    quantity: cartItems[index].quantity,
    addedToCart: true,
    id: cartItems[index].id,
    bundle_items: ungroupBundleItems(
      (cartItems[index] as ICartItem_Product).bundle_items ?? [],
    ),
  };
};
export const BundleProductContextProvider: React.FC<{
  children: ReactNode;
  product: IBundle;
}> = ({ children, product }) => {
  const getCart = useGetCartQuery();
  const compnay = useGetCompanyQuery();
  const { enableAddToCart } = useSellingMode();
  const { theme } = compnay.data?.result ?? {};

  const { show_add_to_card_btn, add_to_cart_btn_title } =
    theme?.components?.product ?? {};

  const cartItems = getCart?.data?.result?.items;

  const [quantity, setQuantity] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  const [selectedBundleItems, setSelectedBundleItems] = useState<
    ISelectedBundleItems[]
  >([]);

  const currentProductInCart = getProductFromCart(
    cartItems ?? [],
    product,
    selectedBundleItems,
  );
  const cartItemId = currentProductInCart?.id;

  const {
    has_required_addons,
    has_term_and_condition,
    has_required_fields,
    units,
    is_configurable,
    has_configurable_parent,
    quantity_on_hand,
    min_quantity_in_cart,
    is_disable,
  } = product;

  const show_all_product_units =
    theme?.components?.product?.show_all_product_units;

  const mustViewDetails =
    has_required_addons ||
    has_term_and_condition ||
    has_required_fields ||
    (!!units.length && show_all_product_units);

  const viewDetails =
    mustViewDetails || is_configurable || has_configurable_parent;

  const isDisabled = !!is_disable && !viewDetails;
  const outOfStock =
    (quantity_on_hand - currentProductInCart.quantity <= 0 ||
      (!!min_quantity_in_cart &&
        min_quantity_in_cart >
          quantity_on_hand - currentProductInCart.quantity)) &&
    !viewDetails;

  const resetProduct = useCallback(() => {
    setAddedToCart(currentProductInCart.addedToCart);
    setQuantity(currentProductInCart.quantity);
  }, [currentProductInCart.addedToCart, currentProductInCart.quantity]);

  useEffect(() => {
    if (getCart.isFetching) return;
    resetProduct();
  }, [getCart?.isFetching, cartItems, resetProduct]);

  useEffect(() => {
    if (!product.bundle_items?.length) return;

    const processedItems = product.bundle_items?.flatMap(generateBundleItems);
    const initSelectedItems: ISelectedBundleItems[] = processedItems?.map(
      (item) => ({
        bundle_item_id: item.id,
        product_id: item.product_id,
        is_configurable: item.product.is_configurable,
        child: null,
      }),
    );
    setSelectedBundleItems(initSelectedItems);
  }, [product?.id]);

  const hanldeChildChange = useCallback(
    (item_index: number, index: number | null) => {
      setSelectedBundleItems((prev) => {
        const changedItem = prev[item_index];
        const changedItemToBut = {
          ...changedItem,
          child:
            index === null || index === -1
              ? null
              : product.bundle_items?.find(
                  (item) => item.id === changedItem.bundle_item_id,
                )?.product?.children[index]?.id,
        };
        const Data = prev.map((item, idx) =>
          idx === item_index ? changedItemToBut : item,
        );
        return Data;
      });
    },
    [product.bundle_items],
  );

  const notAllBundleItemsChildrenSelected =
    selectedBundleItems.filter((item) => item.is_configurable && !item.child)
      .length > 0;

  const value = useMemo(
    (): BundleProductContextType => ({
      product,
      quantity,
      setQuantity,
      addedToCart,
      setAddedToCart,
      enableAddToCart,
      showAddToCardBtn: show_add_to_card_btn,
      viewDetails,
      mustViewDetails,
      add_to_cart_btn_title,
      cartItemId,
      outOfStock,
      isDisabled,
      currentProductInCart,
      selectedBundleItems,
      setSelectedBundleItems,
      hanldeChildChange,
      notAllBundleItemsChildrenSelected,
    }),
    [
      product,
      quantity,
      addedToCart,
      enableAddToCart,
      theme,
      currentProductInCart,
      selectedBundleItems,
      notAllBundleItemsChildrenSelected,
      hanldeChildChange,
    ],
  );

  return (
    <BundleProductContext.Provider value={value}>
      {children}
    </BundleProductContext.Provider>
  );
};

export const useBundleProduct = (): BundleProductContextType => {
  const context = useContext(BundleProductContext);
  if (context === undefined) {
    throw new Error(
      "useBundleProduct must be used within a BundleProductProvider",
    );
  }
  return context;
};
